/* @flow */
import cx from 'classnames';
import * as React from 'react';
import ReactDOM from 'react-dom';

import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import createChainedFunction from 'utils/createChainedFunction';
import Dialog from 'components/Dialog.jsx';
import focusNode from 'utils/focusNode';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import errorLogger from 'utils/errorLogger';

import './styles/ConfirmDialog.scss';

import type { Props as DialogProps } from 'components/Dialog.jsx'; // eslint-disable-line no-unused-vars

// TODO
// * Reconcile `action*` & `confirm*` prop & method names
// * Fix `className`s

// prettier-ignore
type Props = {
  actionButtonColor?: string,
  actionButtonText?: string,
  cancelButtonText?: string,
  cancelEventKey?: ?string,
  cancelEventProperties?: ?Object,
  children?: React.Node,
  /** Disable action button */
  confirmDisabled?: boolean,
  confirmEventKey?: ?string,
  confirmEventProperties?: ?Object,
  /** `border` between `header` & body */
  divided: boolean,
  eventContext?: string,
  eventScreenName?: string,
  hideCancel: boolean,
  /** Cancel button action */
  onCancel?: Function,
  /** Action button action */
  onConfirm?: Function,
  preConfirmPromise?: Function,
  staticModal: boolean,
  /* :: ...$Exact<DialogProps>, */
};

type State = {
  loading: boolean,
};

export default class ConfirmDialog extends React.Component<Props, State> {
  static defaultProps = {
    ...Dialog.defaultProps,
    actionButtonText: 'Yes',
    cancelButtonText: 'No',
    divided: false,
    hideCancel: false,
    onConfirm: () => {},
    preConfirmPromise: () => Promise.resolve(),
    size: 'xsmall',
    staticModal: true,
  };

  state: State = {
    loading: false,
  };

  handleCancelButtonClick = () => {
    const { onCancel, onRequestHide } = this.props;

    // $FlowFixMe
    onRequestHide();

    if (onCancel) {
      onCancel();
    }
  };

  handleConfirmButtonClick = () => {
    const { onConfirm, onRequestHide, preConfirmPromise, staticModal } = this.props;

    this.setState({ loading: true });

    if (staticModal) {
      // $FlowFixMe
      preConfirmPromise()
        .then(onRequestHide)
        .then(onConfirm)
        .catch(errorLogger('Static modal pre confirm failure'));
    } else {
      // $FlowFixMe
      preConfirmPromise()
        .then(onConfirm)
        .catch(errorLogger('Pre confirm failure'));
    }
  };

  renderBody = () => {
    const {
      actionButtonColor,
      actionButtonText,
      cancelButtonText,
      cancelEventKey,
      cancelEventProperties,
      children,
      confirmDisabled,
      confirmEventKey,
      confirmEventProperties,
      eventContext,
      eventScreenName,
      hideCancel,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <LoadingIndicator />;
    } else {
      return (
        <div>
          <div className={cx({ 'ConfirmDialog-text': !!children })}>{children}</div>
          <Buttons axis="y">
            <Button
              color={actionButtonColor}
              disabled={confirmDisabled}
              key="confirm-action"
              ref={component => focusNode(ReactDOM.findDOMNode(component))}
              className="action"
              onClick={this.handleConfirmButtonClick}
              eventContextName={eventContext}
              eventElementName="ConfirmDialog-actionButton"
              eventScreenName={eventScreenName}
              eventKey={confirmEventKey}
              eventProperties={confirmEventProperties}
              data-test-selector="confirm_dialog_action_button">
              {actionButtonText}
            </Button>
            {!hideCancel && (
              <Button
                level="secondary"
                key="confirm-cancel"
                className="cancel"
                onClick={this.handleCancelButtonClick}
                eventContextName={eventContext}
                eventElementName="ConfirmDialog-cancelButton"
                eventKey={cancelEventKey}
                eventProperties={cancelEventProperties}
                eventScreenName={eventScreenName}>
                {cancelButtonText}
              </Button>
            )}
          </Buttons>
        </div>
      );
    }
  };

  render() {
    const { className, divided, onCancel, onRequestHide, ...props } = this.props;

    return (
      <Dialog
        {...props}
        className={cx('ConfirmDialog', className)}
        onRequestHide={createChainedFunction(onCancel, onRequestHide)}>
        <BoxBody divided={divided}>{this.renderBody()}</BoxBody>
      </Dialog>
    );
  }
}
